import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button, Table, Select } from "antd";
import Layout from "../../Layout";
import CustomerDetailDrawer from "./components/CustomerDetailDrawer";

interface CSATItem {
    key: string;
    date: string;
    customerCode: string;
    customerName: string;
    phone: string;
    category: "Clinic" | "Newhair" | "PTTM" | "Partnership";
    score: number;
    customerNote: string;
    handlerAction: string;
}

export default function KhaoSatPage() {
    // State quản lý Drawer chi tiết khách hàng bên phải
    const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);

    // State trạng thái nhân viên CSKH góc dưới
    const [staffStatus, setStaffStatus] = useState<"available" | "break" | "offline">("available");

    // Dữ liệu mẫu phiếu khảo sát CSAT gần nhất chuẩn theo hình
    const csatData: CSATItem[] = [
        {
            key: "1",
            date: "14/10",
            customerCode: "KH-17311",
            customerName: "Chị H.T.M",
            phone: "090***0928",
            category: "Clinic",
            score: 5,
            customerNote: "Nhân viên nhiệt tình",
            handlerAction: "—"
        },
        {
            key: "2",
            date: "13/10",
            customerCode: "KH-12087",
            customerName: "Chị Đ.T.L",
            phone: "091***4436",
            category: "Clinic",
            score: 3,
            customerNote: "Bị đổi lịch sát giờ",
            handlerAction: "Tự tạo phiếu KN-0411 (R11)"
        },
        {
            key: "3",
            date: "12/10",
            customerCode: "KH-14421",
            customerName: "Anh L.V.T",
            phone: "098***2204",
            category: "Newhair",
            score: 4,
            customerNote: "—",
            handlerAction: "—"
        }
    ];

    // Cấu hình các cột cho bảng Phiếu gần nhất
    const columns = [
        {
            title: "Ngày",
            dataIndex: "date",
            key: "date",
            width: 100,
            render: (text: string) => <span className="text-gray-800 dark:text-gray-200">{text}</span>
        },
        {
            title: "Khách",
            key: "customer",
            width: 180,
            render: (_: any, record: CSATItem) => (
                <span
                    onClick={() => {
                        setSelectedCustomer({
                            code: record.customerCode,
                            name: record.customerName,
                            phone: record.phone,
                            serviceCategory: record.category,
                            csCode: "CS2"
                        });
                    }}
                    className="font-medium text-[#1d4c7d] dark:text-blue-400 cursor-pointer hover:underline block leading-tight"
                >
                    {record.customerCode} · {record.customerName}
                </span>
            )
        },
        {
            title: "Mảng",
            dataIndex: "category",
            key: "category",
            width: 90,
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300 font-medium">{text}</span>
        },
        {
            title: "Điểm",
            dataIndex: "score",
            key: "score",
            width: 80,
            render: (score: number) => (
                <span className={score <= 3 ? "text-amber-600 font-bold dark:text-amber-400" : "text-gray-800 dark:text-gray-200"}>
                    {score}
                </span>
            )
        },
        {
            title: "Ghi chú khách",
            dataIndex: "customerNote",
            key: "customerNote",
            width: 250,
            render: (text: string) => <span className="text-gray-800 dark:text-gray-200">{text}</span>
        },
        {
            title: "Xử lý",
            dataIndex: "handlerAction",
            key: "handlerAction",
            width: 200,
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300">{text}</span>
        }
    ];

    const getStatusDotColor = () => {
        switch (staffStatus) {
            case "available": return "#52c41a";
            case "break": return "#d48806";
            case "offline": return "#8c8c8c";
            default: return "#52c41a";
        }
    };

    return (
        <Layout>
            <Helmet>
                <title>Khảo sát — CSAT sau chăm sóc | Khothi</title>
            </Helmet>

            <div className="min-h-screen text-xs flex flex-col justify-between transition-colors duration-200">
                <div className="flex flex-1 overflow-hidden gap-4">
                    
                    {/* KHUNG NỘI DUNG CHÍNH BÊN TRÁI */}
                    <div className="space-y-4 overflow-y-auto pb-20 w-full">
                        
                        {/* 1. Header Tiêu đề & Quy tắc */}
                        <div className="flex flex-wrap items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-2 gap-2">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 border-l-4 border-[#1d4c7d] pl-2">
                                <span className="font-bold text-[#1d4c7d] dark:text-blue-400 text-sm">
                                    Khảo sát — CSAT sau chăm sóc
                                </span>
                                <span className="text-gray-600 dark:text-gray-400">
                                    Vai trò: CSKH
                                </span>
                                <span className="text-gray-500 dark:text-gray-400 hidden xl:inline">
                                    Quy tắc: R11 CSAT ≤ 3 → phiếu khiếu nại · SOP CSKH mục 5
                                </span>
                            </div>
                        </div>

                        {/* 2. Dòng Khung Cam DỮ LIỆU MINH HOẠ */}
                        <div className="border border-dashed border-orange-400 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 text-center py-1 font-bold tracking-wider rounded text-xs">
                            DỮ LIỆU MINH HOẠ — tên khách, mã nhân viên và mọi con số là giả
                        </div>

                        {/* 3. BỐ CỤC 2 CỘT CHÍNH (PHÂN BỐ ĐIỂM & PHIẾU GẦN NHẤT) */}
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                            
                            {/* Cột trái: Phân bố điểm (tuần) */}
                            <div className="lg:col-span-5 bg-white dark:bg-[#1f1f1f] rounded border border-gray-200 dark:border-gray-800 shadow-sm p-3.5 space-y-3">
                                <div className="font-bold text-[#1d4c7d] dark:text-blue-400 text-xs border-b border-gray-100 dark:border-gray-800 pb-2">
                                    Phân bố điểm (tuần)
                                </div>
                                
                                <div className="space-y-2">
                                    {[
                                        { star: 5, count: 21, width: "85%" },
                                        { star: 4, count: 11, width: "45%" },
                                        { star: 3, count: 2, width: "15%", urgent: true },
                                        { star: 2, count: 0, width: "0%" },
                                        { star: 1, count: 0, width: "0%" },
                                    ].map((item) => (
                                        <div key={item.star} className="flex items-center gap-2">
                                            <span className="w-3 text-right font-medium text-gray-700 dark:text-gray-300">{item.star}</span>
                                            <div className="flex-1 bg-gray-100 dark:bg-gray-800 h-4 rounded overflow-hidden relative flex items-center">
                                                <div 
                                                    className={`h-full rounded ${item.urgent ? "bg-amber-600" : "bg-[#1d4c7d]"}`}
                                                    style={{ width: item.width }}
                                                ></div>
                                            </div>
                                            <span className="w-6 text-left text-gray-600 dark:text-gray-400 font-medium">{item.count}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="text-gray-600 dark:text-gray-400 text-[11px] pt-1 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                                    <span>Trung bình 4,4 · 34 phiếu. Kênh: Zalo. Cách nhập vào CMS</span>
                                    <span className="bg-[#d97706] text-white text-[10px] px-1 py-0.2 rounded font-bold cursor-pointer">[?]</span>
                                </div>
                            </div>

                            {/* Cột phải: Phiếu gần nhất */}
                            <div className="lg:col-span-7 space-y-2">
                                <div className="font-bold text-[#1d4c7d] dark:text-blue-400 text-xs">
                                    Phiếu gần nhất
                                </div>
                                <div className="bg-white dark:bg-[#1f1f1f] rounded border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                                    <Table
                                        columns={columns}
                                        dataSource={csatData}
                                        pagination={false}
                                        size="small"
                                        className="custom-csat-table"
                                        scroll={{ x: "max-content" }}
                                        rowClassName={(record, index) =>
                                            index % 2 === 1
                                                ? "bg-slate-50/80 dark:bg-[#252525]"
                                                : "bg-white dark:bg-[#1f1f1f]"
                                        }
                                    />
                                </div>
                            </div>

                        </div>

                    </div>

                    {/* 4. DRAWER CHI TIẾT KHÁCH HÀNG BÊN PHẢI */}
                    {selectedCustomer && (
                        <CustomerDetailDrawer
                            customer={selectedCustomer}
                            onClose={() => setSelectedCustomer(null)}
                        />
                    )}
                </div>

                {/* Floating Widget Thanh Trạng Thái (PC 1 hàng, Mobile tự động xuống 2 hàng) */}
                <div className="sticky bottom-3 z-27 sm:mx-0 bg-[#1d4c7d] dark:bg-[#142f4c] text-white rounded-lg shadow-xl px-3.5 py-2.5 flex flex-col sm:flex-row items-center justify-between sm:justify-start gap-2 sm:gap-3 w-full sm:w-fit border border-[#3E91AB]">
                    
                    {/* Phần thông tin nhân viên và Select trạng thái */}
                    <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-2">
                        <div className="flex items-center gap-2">
                            <span
                                className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                                style={{ backgroundColor: getStatusDotColor() }}
                            ></span>
                            <span className="font-bold text-sm whitespace-nowrap">
                                NV-07 · CSKH
                            </span>
                        </div>

                        {/* Trên mobile hiển thị Select ở đây cho cân đối dòng 1 */}
                        <div className="block sm:hidden">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[110px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>
                    </div>

                    {/* Phần Select (cho PC) và các nút bấm hành động */}
                    <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto border-t border-blue-400/30 pt-2 sm:border-t-0 sm:pt-0">
                        
                        {/* Trên PC hiển thị Select ở vị trí này */}
                        <div className="hidden sm:block">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[125px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>

                        <span className="text-xs text-gray-200 hidden md:inline whitespace-nowrap">
                            Không có cuộc gọi
                        </span>

                        <div className="flex items-center gap-1.5 w-full sm:w-auto justify-end">
                            <Button
                                size="small"
                                className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial"
                            >
                                Mô phỏng gọi đến
                            </Button>

                            <Button
                                size="small"
                                className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial"
                            >
                                Gọi số mới
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}